import type { MatchFormat } from "@golazo/shared";
import { ColorMatrixFilter, Container } from "pixi.js";
import {
  DEFAULT_PITCH_SKIN,
  FIELD,
  LOCKSTEP_HASH_INTERVAL,
  type Ball,
  type MatchState,
  type PitchSkinId,
  type Player,
  type Possession,
} from "@golazo/shared";
import type { AiController, AiWorld } from "../ai/brain.js";
import { lerp } from "../core/math.js";
import { createClipRecorder, type Clip } from "./clipRecorder.js";
import {
  createKeyboardInput,
  KEYS_PLAYER_ONE,
  KEYS_PLAYER_TWO,
  type InputSource,
} from "../input/keyboard.js";
import { createGamepadInput } from "../input/gamepad.js";
import { createCompositeInput } from "../input/compositeInput.js";
import { createMouseAim } from "../input/mouseAim.js";
import { currentControls } from "../input/bindings.js";
import { createTouchControls, type TouchControls } from "../input/touchControls.js";
import { wantsTouchControls } from "../mobile/detect.js";
import { haptic } from "../mobile/haptics.js";
import { createTouchCoach, type TouchCoach } from "../ui/touchCoach.js";
import { createAmbientLayer } from "../render/ambient.js";
import { createAimLayer } from "../render/aim.js";
import { createBallLayer, type BallLayer } from "../render/ball.js";
import { createField } from "../render/field.js";
import { createPlayersLayer, type PlayersLayer } from "../render/players.js";
import { cameraFitsField, setCameraOverride } from "../render/cameraModes.js";
import { createRadarLayer } from "../render/radar.js";
import { createScreenFx } from "../render/screenFx.js";
import { createStadiumBackdrop, type StadiumDressing } from "../render/stadium.js";
import { createTrailsLayer } from "../render/trails.js";
import { createVarLineLayer } from "../render/varLine.js";
import { createHud, type Hud } from "../ui/hud.js";
import { computePlayViewport } from "../mobile/viewport.js";
import { cameraFor, visibleField } from "../render/cameraFrame.js";
import { cameraTargetId, createFollowCamera, netZoom, offsideFocus } from "../render/followCamera.js";
import { createJuice, type JuiceView } from "../juice/controller.js";
import type { LockstepClient } from "../online/lockstep.js";
import { stateHash } from "../online/stateHash.js";
import { createSimulation, type SimOptions } from "./simulation.js";
import { replayProgress, varStage } from "./replay.js";
import { createMatchStats, type GoalEvent, type MatchStatsSnapshot } from "./matchStats.js";
import { createScoreboard } from "../ui/scoreboard.js";

/**
 * Ticks de red que se simulan como maximo en un frame al ir por detras.
 *
 * Tiene que ser MUY superior a los 60 ticks/s que produce el server, porque el
 * navegador limita el rAF de una pestaña en segundo plano a ~1 fps: con un tope
 * de 20 el cliente solo simularia 20 ticks/s y se quedaria atras para siempre.
 * Con 600 (10 s de partido por frame) aguanta hasta 0,1 fps y aun asi acota el
 * trabajo de un solo frame.
 */
const MAX_CATCHUP_TICKS = 600;

export interface Match {
  root: Container;
  readonly players: readonly Player[];
  readonly ball: Ball;
  readonly possession: Possession;
  readonly state: MatchState;
  readonly brains: readonly AiController[];
  readonly aiWorld: AiWorld;
  tick(dt: number): void;
  render(alpha: number): void;
  /** Ficha de cada jugador y MVP. Se lee al terminar, para el resumen. */
  stats(): MatchStatsSnapshot;
  /** Jugada del gol para el resumen: la tuya si la hay, si no la ultima. */
  goalClip(): Clip | null;
  /** Jugador que lleva el humano, para marcar "eres tu" en el resumen. */
  readonly humanId: string;
  /**
   * Salta la celebracion y la repeticion (solo sin red). Ver
   * `Simulation.skipGoalPause`.
   */
  skipGoalPause(): void;
  resize(screenWidth: number, screenHeight: number): void;
  /** Pausa audio del partido (pestaña oculta / overlay). */
  suspend(): void;
  resume(): void;
  dispose(): void;
}

export type { SimHumanSeat as MatchHumanSeat } from "./simulation.js";

export interface MatchNetworkOptions {
  lockstep: LockstepClient;
  onDisconnect?: () => void;
}

export type MatchOptions = SimOptions & {
  /** Formato que dicta la sala online. Vacio en local. */
  roomFormat?: Partial<MatchFormat>;
  network?: MatchNetworkOptions;
  /** Skin visual del cesped. Solo render local. */
  pitchSkin?: PitchSkinId;
  /** Grada de un estadio de la carrera. Solo render; en online no existe. */
  stadium?: StadiumDressing;
  /**
   * Dos en el mismo ordenador: el asiento del jugador 2 (el 1 es `humanId`).
   * Tiene que venir tambien en `humanSeats`. Solo en local, nunca en online.
   */
  secondPlayerId?: string;
  /**
   * Hubo cuerpo: `check` = `by` embistio a `victim`; `block` = `victim` estaba
   * plantado y rebota `by`. Para los consejos de la primera vez (la O); no
   * afecta a nada del partido.
   */
  onBodyContact?: (kind: "check" | "block", byId: string, victimId: string) => void;
  /** Has disparado tu (para el consejo del aftertouch). No afecta al partido. */
  onHumanShot?: () => void;
  /**
   * Pistas del mando tactil (pulgares fantasma) en este partido. Solo se ven
   * con el mando tactil puesto; ver `ui/touchCoach.ts`.
   */
  touchCoach?: boolean;
};

export function createMatch(seedOrOptions: number | MatchOptions = Date.now() >>> 0): Match {
  const options: MatchOptions =
    typeof seedOrOptions === "number" ? { seed: seedOrOptions } : seedOrOptions;

  const root = new Container();
  const skinId = options.pitchSkin ?? DEFAULT_PITCH_SKIN;

  const stadium = createStadiumBackdrop(skinId, options.stadium);
  root.addChild(stadium.container);

  const camera = new Container();
  camera.label = "camera";
  root.addChild(camera);

  /*
   * Filtro de repeticion (lo pidieron jugadores en Discord, 2026-09-28): la
   * imagen de un clip de television — color algo apagado y calido y un punto
   * mas de contraste — sobre el campo y el estadio, NO sobre el marcador. Las
   * lineas de barrido las pone el HUD. Entra y sale con las barras de cine y,
   * fuera de la repeticion, no hay filtro puesto: no cuesta nada jugando.
   */
  const replayGrade = new ColorMatrixFilter();
  replayGrade.saturate(-0.38, false);
  replayGrade.contrast(0.14, true);
  replayGrade.tint(0xfff0dc, true);
  const replayGraded = [stadium.container, camera];
  let gradeT = 0;
  let gradeOn = false;
  const syncReplayGrade = (dt: number): void => {
    const cinematic = replayProgress(sim.state) > 0 || varStage(sim.state) === "freeze";
    // Los mismos tiempos que las barras (`hud.ts`): entra en 0,28 s y sale en 0,18 s.
    gradeT = cinematic ? Math.min(1, gradeT + dt / 0.28) : Math.max(0, gradeT - dt / 0.18);
    const on = gradeT > 0.001;
    if (on !== gradeOn) {
      gradeOn = on;
      for (const layer of replayGraded) layer.filters = on ? [replayGrade] : null;
    }
    if (on) replayGrade.alpha = gradeT < 0.5 ? 2 * gradeT * gradeT : 1 - (1 - gradeT) * (1 - gradeT) * 2;
  };

  const field = createField(skinId);
  field.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(field.container);

  const ambient = createAmbientLayer(skinId);
  ambient.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(ambient.container);

  let screenWidth = 800;
  let screenHeight = 600;

  const trails = createTrailsLayer();
  const screenFx = createScreenFx();
  const juice: JuiceView = createJuice({ stadium, screenFx, trails });

  // La simulacion se crea con los hooks ya enganchados, pero las capas de
  // render que necesitan no existen todavia; se resuelven perezosamente.
  let playersLayer: PlayersLayer | null = null;
  let aimLayer: ReturnType<typeof createAimLayer> | null = null;
  /** La mira del jugador 2, si hay dos en el mismo ordenador. */
  let aimLayer2: ReturnType<typeof createAimLayer> | null = null;
  const aimFor = (playerId: string): ReturnType<typeof createAimLayer> | null => {
    if (playerId === sim.humanId) return aimLayer;
    if (playerId === options.secondPlayerId) return aimLayer2;
    return null;
  };

  // Se declara antes que la simulacion porque sus hooks lo referencian, pero
  // solo puede construirse DESPUES (necesita `sim.players`). Los hooks unicamente
  // corren dentro del tick, cuando ya esta asignado — de ahi el opcional.
  let stats: ReturnType<typeof createMatchStats> | null = null;
  /** Ultimos segundos de juego, siempre en memoria, para quedarnos con el gol. */
  const clips = createClipRecorder();
  /** Gol del humano (mejor recuerdo) y ultimo gol (recambio si no marco). */
  let humanClip: Clip | null = null;
  let lastClip: Clip | null = null;

  /*
   * El movil vibra con TU partido (solo con el mando tactil puesto): el balon
   * que te llega, la entrada que te lo quita, el choque, el palo, el gol. Solo
   * jugando: la repeticion y el VAR no se sienten dos veces.
   */
  let touchFeel = false;
  let coach: TouchCoach | null = null;
  const feel = (kind: Parameters<typeof haptic>[0]): void => {
    if (touchFeel && sim.state.phase === "playing") haptic(kind);
  };

  const sim = createSimulation(options, {
    // Cada mira solo atiende a SU jugador: en online avisan todos los humanos.
    onShotLock: (dir, kind, playerId) => aimFor(playerId)?.lockShot(dir.x, dir.y, kind),
    onShotRelease: (playerId) => aimFor(playerId)?.clearShotLock(),
    onDash: (x, y, fx, fy, side) => juice.onDash(x, y, fx, fy, side),
    onTackle: (x, y) => juice.onTackle(x, y),
    onNutmeg: (by, victim) => juice.onNutmeg(by.pos.x, by.pos.y, victim.pos.x, victim.pos.y),
    onBodyCheck: (by, victim) => {
      juice.onBodyCheck(by.pos.x, by.pos.y, victim.pos.x, victim.pos.y);
      options.onBodyContact?.("check", by.id, victim.id);
      if (by.id === sim.humanId || victim.id === sim.humanId) feel("bump");
    },
    onBodyBlock: (by, victim) => {
      juice.onBodyBlock(by.pos.x, by.pos.y, victim.pos.x, victim.pos.y);
      options.onBodyContact?.("block", by.id, victim.id);
    },
    onFirstTimeWhiff: (by) => juice.onFirstTimeWhiff(by.pos.x, by.pos.y),
    onCut: (by) => juice.onCut(by.pos.x, by.pos.y),
    onFirstTime: (_grade, by, _kind, quality) =>
      juice.onFirstTime(by.pos.x, by.pos.y, quality),
    onKick: (kind, by, power) => {
      stats?.recordKick(kind, by);
      juice.onKick(kind, by.pos.x, by.pos.y, by.side, power);
      if (kind === "shoot" && by.id === sim.humanId) options.onHumanShot?.();
      if (by.id === sim.humanId) coach?.onKick();
    },
    onPossessionLost: (kind, from, by) => {
      stats?.recordLoss(from, by);
      if (from.id === sim.humanId && kind !== "carry") feel("tackled");
    },
    onSidelineBump: (x, y, intensity) => juice.onSidelineBump(x, y, intensity),
    onPostHit: (x, y, speed) => {
      juice.onPostHit(x, y, speed);
      feel("post");
    },
    // El local defiende la porteria izquierda: si encaja el, la red que se
    // mueve es esa.
    onGoalNet: (conceded, y, speed) =>
      field.nets.hit(conceded === "home" ? "left" : "right", y, speed),
  });

  stats = createMatchStats(sim.players);
  /** Quien llevaba el balon el tick anterior (para sentir cuando te llega). */
  let lastOwnerId: string | null = null;
  /** Segundos jugados, para fechar los goles en el resumen. */
  let matchClock = 0;
  const scoreboard = createScoreboard();

  const onTabKey = (event: KeyboardEvent): void => {
    if (event.code !== "Tab") return;
    // TAB mueve el foco del navegador por defecto; aqui es el marcador.
    event.preventDefault();
    scoreboard.setTableVisible(event.type === "keydown");
  };
  window.addEventListener("keydown", onTabKey);
  window.addEventListener("keyup", onTabKey);

  const humanPlayer = (): Player => sim.players.find((p) => p.id === sim.humanId)!;

  trails.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(trails.container);

  // Encima del cesped y por debajo de las fichas: la linea del VAR se lee como
  // cal pintada sobre el campo, no como un adorno pegado a la pantalla.
  const varLine = createVarLineLayer();
  varLine.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(varLine.container);

  // El jugador 2 solo sin red: en online cada uno esta en su ordenador.
  const pairId = !options.network && options.secondPlayerId ? options.secondPlayerId : null;
  const pairPlayer = (): Player | null => (pairId ? (sim.players.find((p) => p.id === pairId) ?? null) : null);
  playersLayer = createPlayersLayer(sim.players, sim.humanId, pairId ? [pairId] : []);
  playersLayer.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(playersLayer.container);

  const ballLayer: BallLayer = createBallLayer(sim.ball);
  ballLayer.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(ballLayer.container);

  // La mira simula el vuelo con el balon y la asistencia de ESTE partido.
  const aimOptions = { ball: sim.ball, assistSpreadScale: sim.assist.spreadScale };
  aimLayer = createAimLayer(humanPlayer(), aimOptions);
  aimLayer.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(aimLayer.container);
  const second = pairPlayer();
  if (second) {
    aimLayer2 = createAimLayer(second, aimOptions);
    aimLayer2.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
    camera.addChild(aimLayer2.container);
  }

  juice.particles.container.position.set(FIELD.MARGIN, FIELD.MARGIN);
  camera.addChild(juice.particles.container);

  /*
   * Pantalla <-> campo, para el raton y el dedo. Por el contenedor de los
   * JUGADORES y no por la camara: el campo va desplazado `FIELD.MARGIN` dentro
   * de la camara, y medirlo contra ella apuntaba 90 u de lado (hasta ~8° a
   * 600 u: el clic "al grado" no lo era).
   */
  const fieldPoint = (clientX: number, clientY: number, canvas: HTMLCanvasElement): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    return playersLayer!.container.toLocal({ x: clientX - rect.left, y: clientY - rect.top });
  };
  const screenPoint = (x: number, y: number, canvas: HTMLCanvasElement): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    const at = playersLayer!.container.toGlobal({ x, y });
    return { x: at.x + rect.left, y: at.y + rect.top };
  };

  root.addChild(screenFx.container);

  const hud: Hud = createHud();
  root.addChild(hud.container);

  const radar = createRadarLayer(options.pitchSkin ?? DEFAULT_PITCH_SKIN);
  radar.setVisible(sim.assist.radar);
  root.addChild(radar.container);

  const lockstep = options.network?.lockstep ?? null;
  /*
   * Solo contra bots se juega en local, sin el retraso del lockstep. Ya no es
   * una constante del partido: en cuanto entra alguien, `lockstep.solo` pasa a
   * false (desde el manejador del socket) y el siguiente frame ya va por
   * lockstep. Antes se decidia una vez al empezar y el anfitrion seguia jugando
   * su partido local para siempre, sin ver al que entraba — que veia otro.
   */
  const soloOnline = (): boolean => Boolean(lockstep?.solo && options.localPlayerId);

  let touch: TouchControls | null = null;
  /** Con el raton golpeando, el cursor del campo es una mira. */
  let mouseCursor = false;
  let input: InputSource;
  /** El del jugador 2 (su mitad del teclado y el segundo mando). */
  let input2: InputSource | null = null;
  const keyboard = createKeyboardInput(window, {
    onChange: () => {
      if (!lockstep || !options.localPlayerId || soloOnline()) return;
      lockstep.submitLocal(input.consume());
    },
    // Con dos, cada uno en su mitad: el 1 a la izquierda.
    bindings: pairId ? KEYS_PLAYER_ONE : undefined,
  });
  // El primer mando es siempre del jugador 1, tambien jugando solo.
  const pad = createGamepadInput(0);

  if (pairId) {
    input = createCompositeInput([keyboard, pad]);
    input2 = createCompositeInput([
      createKeyboardInput(window, { bindings: KEYS_PLAYER_TWO }),
      createGamepadInput(1),
    ]);
    // Los dos tienen que caber: se ve el campo entero (sin tocar la
    // preferencia guardada de cuando se juega solo).
    setCameraOverride("fit");
  } else if (wantsTouchControls()) {
    touch = createTouchControls({
      field: {
        toWorld: fieldPoint,
        toScreen: screenPoint,
        player: () => sim.players.find((p) => p.id === sim.humanId) ?? null,
        // Con el balon, o con uno que llega para rematarlo de primera. Sin eso,
        // tocar el campo no hace nada: una entrada sin querer cuesta mas que
        // un toque que no responde.
        canStrike: () => sim.possession.ownerId === sim.humanId || sim.firstTimeWindow() !== null,
        ballReady: () =>
          sim.possession.ownerId === sim.humanId &&
          (sim.players.find((p) => p.id === sim.humanId)?.trapTime ?? 1) <= 0,
      },
      onChange: () => {
        if (!lockstep || !options.localPlayerId || soloOnline()) return;
        lockstep.submitLocal(input.consume());
      },
    });
    input = createCompositeInput([keyboard, touch, pad]);
    touchFeel = true;
    if (options.touchCoach) coach = createTouchCoach(touch.element);
  } else if (currentControls().mouseAim) {
    /*
     * Golpear con el raton (CONTROLES): clic hacia el cursor. Va PRIMERO en la
     * mezcla, pero solo dice algo mientras el boton esta pulsado; el resto del
     * tiempo mueve el teclado. El cursor se pasa al campo con `fieldPoint`.
     */
    const mouse = createMouseAim({
      toWorld: fieldPoint,
      player: () => sim.players.find((p) => p.id === (options.localPlayerId ?? sim.humanId)) ?? null,
      onChange: () => {
        if (!lockstep || !options.localPlayerId || soloOnline()) return;
        lockstep.submitLocal(input.consume());
      },
    });
    input = createCompositeInput([mouse, keyboard, pad]);
    mouseCursor = true;
  } else {
    input = createCompositeInput([keyboard, pad]);
  }

  if (touch) touch.show();
  // Con el raton golpeando, el cursor sobre el campo es una mira (matchChrome.css).
  if (mouseCursor) document.documentElement.classList.add("fynta-mouse-aim");

  lockstep?.onDisconnect(() => {
    options.network?.onDisconnect?.();
  });
  // El apodo del que entra, en su jugador. Solo se dibuja: no entra en la huella.
  // Entero: el recorte para que quepa en el campo lo hace quien lo pinta, y la
  // tabla final tiene que decir el nombre exacto.
  lockstep?.onPeerJoined((playerId, nickname) => {
    const player = sim.players.find((p) => p.id === playerId);
    if (player && player.role !== "gk") player.nickname = nickname;
  });
  // Y el del que se va, fuera: su jugador lo lleva la IA desde ese momento, y
  // la tabla final solo nombra a quien TERMINO el partido.
  lockstep?.onPeerLeft((playerId) => {
    const player = sim.players.find((p) => p.id === playerId);
    if (player) player.nickname = null;
  });

  if (lockstep && !soloOnline()) {
    const catchUpDt = 1 / 60;
    lockstep.replayCatchUp((frames) => sim.tickMulti(catchUpDt, frames));
  }

  const unlockAudio = (): void => {
    juice.audio.unlock();
    window.removeEventListener("keydown", unlockAudio);
    window.removeEventListener("pointerdown", unlockAudio);
  };
  window.addEventListener("keydown", unlockAudio);
  window.addEventListener("pointerdown", unlockAudio);

  let lastRenderTime = 0;

  const follow = createFollowCamera();
  /** Lo que sigue la camara con dos jugadores: su punto medio (ver `applyCamera`). */
  const pairTarget = { id: sim.humanId, pos: { x: 0, y: 0 }, prevPos: { x: 0, y: 0 } };
  /**
   * A quien mira la repeticion: el que la metio, no el que llevas tu.
   *
   * Una repeticion desde la camara de tu jugador enseña la espalda de quien
   * marco — o nada, si el gol cayo lejos. Es la diferencia entre "aqui esta el
   * gol" y "aqui estabas tu mientras alguien marcaba".
   */
  let replayFocusId: string | null = null;
  /** Para cortar a la camara del goleador una sola vez, no en cada frame. */
  let replayCut = false;
  /** Ya se ha cortado al plano del congelado del VAR. */
  let varCut = false;
  /** Trozo de campo visible tras el ultimo `applyCamera`. Lo consume el radar. */
  let seenField: { x: number; y: number; width: number; height: number } | null = null;

  /**
   * Una sola transformada de camara, compuesta en este orden:
   *
   *   encaje a pantalla x acercamiento del modo x zoom del juice
   *   -> punto al que mirar (el jugador que llevas, o el gol si esta mandando)
   *   -> recorte a los limites del campo
   *   -> shake
   *
   * El recorte va DESPUES de elegir el punto, no antes: asi el seguimiento
   * puede pedir lo que quiera y es el encuadre quien decide si cabe. Y en los
   * modos sin acercamiento los dos limites del eje coinciden, con lo que la
   * camara queda clavada exactamente donde estaba antes de existir todo esto.
   */
  const applyCamera = (dt = 0, alpha = 1): void => {
    const view = computePlayViewport(screenWidth, screenHeight);
    const scale = view.scale * juice.camera.zoom;

    const targetId = cameraTargetId(sim.state, sim.humanId, replayFocusId);
    let target = sim.players.find((p) => p.id === targetId) ?? null;
    /*
     * Con dos en el mismo ordenador la camara mira al PUNTO MEDIO entre los dos,
     * no a uno: si alguien acerca el zoom, que no deje fuera al otro de golpe.
     * (Por defecto se ve el campo entero y esto ni se nota.)
     */
    const second = pairPlayer();
    if (second && target && targetId === sim.humanId) {
      pairTarget.pos.x = (target.pos.x + second.pos.x) / 2;
      pairTarget.pos.y = (target.pos.y + second.pos.y) / 2;
      pairTarget.prevPos.x = (target.prevPos.x + second.prevPos.x) / 2;
      pairTarget.prevPos.y = (target.prevPos.y + second.prevPos.y) / 2;
      target = pairTarget as unknown as Player;
    }

    // Corte seco al entrar y al salir de la repeticion: es un plano nuevo, y
    // llegar a el con la panoramica de cambio de jugador parece un error.
    const inReplay = targetId !== sim.humanId;
    if (inReplay !== replayCut) {
      replayCut = inReplay;
      if (target) follow.snap(target.pos.x, target.pos.y);
    }

    /*
     * El congelado del VAR es otro plano, y entra cortando.
     *
     * Llegar deslizandose desde donde estaba el balon convertiria la prueba en
     * una panoramica: cuando la linea entra en cuadro, el congelado ya casi ha
     * terminado. Un corte dice "esto es otra camara", que es exactamente lo
     * que es.
     */
    const varAt = offsideFocus(sim.state);
    if ((varAt !== null) !== varCut) {
      varCut = varAt !== null;
      if (varAt) follow.snap(varAt.x, varAt.y);
    }

    follow.update(dt, target, alpha);

    /*
     * En la repeticion la camara va al BALON, no al goleador.
     *
     * El goleador remata y se va; el balon es el que hace la jugada y el que
     * termina en la red. Seguir al jugador dejaba el gol fuera de plano justo
     * en el momento de entrar — que es lo unico que la repeticion existe para
     * enseñar.
     */
    // En el congelado manda la linea; en el resto de la repeticion, el balon.
    const replaying = inReplay && varAt === null;
    const ballX = lerp(sim.ball.prevPos.x, sim.ball.pos.x, alpha);
    const ballY = lerp(sim.ball.prevPos.y, sim.ball.pos.y, alpha);

    // Y se acerca segun el balon se hunde en la red, para que se vea el paño.
    const zoom = netZoom(ballX, replaying);
    const finalScale = scale * zoom;

    // El gol manda sobre el seguimiento mientras dura su acercamiento.
    const w = juice.camera.focusWeight;
    const baseX = varAt ? varAt.x : replaying ? ballX : follow.x;
    const baseY = varAt ? varAt.y : replaying ? ballY : follow.y;
    const focusX = baseX + (juice.camera.focusX - baseX) * w;
    const focusY = baseY + (juice.camera.focusY - baseY) * w;

    const at = cameraFor(view.rect, finalScale, focusX, focusY);
    camera.scale.set(finalScale);
    camera.x = at.x + juice.shake.offsetX;
    camera.y = at.y + juice.shake.offsetY;
    seenField = visibleField(view.rect, finalScale, at.x, at.y);
    /*
     * Con el campo entero a la vista, el minimapa sobra.
     *
     * Es media razon de ser de ese modo: quien lo elige es justo quien no
     * quiere estar mirando a una esquina para saber donde esta el balon. Y un
     * minimapa que dibuja lo mismo que ya ves, encima del propio campo, es
     * ruido tapando cesped.
     */
    radar.setVisible(sim.assist.radar && !cameraFitsField());

    hud.setCompact(view.compactHud, view.hudTop);
    // El HUD flota sobre el cesped, asi que necesita saber por donde anda el
    // balon para apartarse. La cuenta es la misma transformacion que acaba de
    // aplicarse a la camara.
    hud.setBallScreen(
      camera.x + sim.ball.pos.x * finalScale,
      camera.y + sim.ball.pos.y * finalScale,
    );
  };

  const playerLabel = (id: string | null): { nickname: string | null; shirtNumber: number } | null => {
    if (!id) return null;
    const p = sim.players.find((x) => x.id === id);
    return p ? { nickname: p.nickname, shirtNumber: p.shirtNumber } : null;
  };

  const captureGoalClip = (goal: GoalEvent): void => {
    const clip = clips.freeze({
      scorer: goal.ownGoal ? null : playerLabel(goal.scorerId),
      assist: playerLabel(goal.assistId),
      side: goal.side,
      atSecond: goal.atSecond,
    });
    if (!clip) return;
    lastClip = clip;
    // El gol propio manda sobre cualquier otro: es el que se quiere volver a ver.
    const mine = goal.scorerId === sim.humanId || goal.assistId === sim.humanId;
    if (mine && !goal.ownGoal) humanClip = clip;
  };

  return {
    root,
    players: sim.players,
    ball: sim.ball,
    possession: sim.possession,
    state: sim.state,
    brains: sim.brains,
    aiWorld: sim.aiWorld,

    tick(dt: number): void {
      if (lockstep && options.localPlayerId) {
        const localFrame = input.consume();
        // La botonera y el destello del jugador se enteran de lo que esta
        // pulsado ANTES de simular, y directamente del input: asi encienden en
        // el mismo frame y sin depender de que lleves el balon.
        touch?.reflect(localFrame.held);
        playersLayer?.setKickSignal(
          localFrame.held.has("kick"),
          localFrame.pressed.has("kick"),
        );
        if (soloOnline()) {
          /*
           * Solo vs bots: sin retraso, y cada tick jugado se manda con SU
           * numero para que el servidor lo apunte tal cual. Por `tickMulti`, y
           * con el frame que devuelve `submitSolo` (la copia que viaja), porque
           * es exactamente lo que rehara quien entre despues.
           */
          const played = lockstep.submitSolo(localFrame);
          sim.tickMulti(dt, new Map([[options.localPlayerId, played]]));
        } else {
          lockstep.submitLocal(localFrame);
          // Tope de ticks por frame: tras un paron (pestaña en segundo plano) el
          // backlog puede ser de miles y simularlos de golpe congela el juego.
          // 20/frame ya son 20x la produccion del server, asi que recupera igual.
          let steps = 0;
          while (steps < MAX_CATCHUP_TICKS) {
            const frames = lockstep.pollFrames();
            if (!frames) break;
            const simulatedTick = lockstep.currentTick;
            sim.tickMulti(dt, frames);
            lockstep.advance();
            steps += 1;
            if (simulatedTick % LOCKSTEP_HASH_INTERVAL === 0) {
              const hash = stateHash(sim.players, sim.ball, sim.state);
              lockstep.sendHash(simulatedTick, hash);
              if (import.meta.env.DEV) {
                console.info(`[golazo:sync] t=${simulatedTick} h=${hash}`);
              }
            }
          }
        }

        input.endTick();
      } else if (input2 && pairId) {
        // Dos en el mismo ordenador: los dos van por fuera, como en online.
        const one = input.consume();
        const two = input2.consume();
        playersLayer?.setKickSignal(one.held.has("kick"), one.pressed.has("kick"));
        playersLayer?.setKickSignal(two.held.has("kick"), two.pressed.has("kick"), pairId);
        sim.tickMulti(dt, new Map([[sim.humanId, one], [pairId, two]]));
        input.endTick();
        input2.endTick();
      } else {
        const frame = input.consume();
        touch?.reflect(frame.held);
        playersLayer?.setKickSignal(frame.held.has("kick"), frame.pressed.has("kick"));
        sim.tick(dt, frame);
        input.endTick();
      }

      if (sim.state.phase !== "goal") {
        juice.observe(sim.ball, sim.possession, sim.state, sim.aiWorld.tactics, dt);
      }

      if (sim.state.phase === "playing") matchClock += dt;
      // Se graba siempre: cuando cae el gol ya es tarde para empezar.
      clips.sample(sim.players, sim.ball, dt);
      const goal = stats?.update(sim.state.score, sim.possession.ownerId, matchClock);
      // El balon te llega: un toque corto, como el control en el pie.
      const owner = sim.possession.ownerId;
      if (owner !== lastOwnerId && owner === sim.humanId) {
        feel("receive");
        if (sim.state.phase === "playing") coach?.onBall();
      }
      lastOwnerId = owner;
      if (goal && touchFeel) {
        const mine = sim.players.find((p) => p.id === sim.humanId)?.side;
        haptic(goal.side === mine ? "goal" : "conceded");
      }
      if (goal && stats) {
        scoreboard.announceGoal(goal, stats.snapshot(), sim.humanId);
        captureGoalClip(goal);
        // Vale tambien para el gol en propia: `scorerId` es quien la metio.
        replayFocusId = goal.scorerId;
      }

    },

    get humanId(): string {
      return sim.humanId;
    },

    goalClip(): Clip | null {
      return humanClip ?? lastClip;
    },

    stats(): MatchStatsSnapshot {
      return stats
        ? stats.snapshot()
        : { players: [], goals: [], mvp: null };
    },

    render(alpha: number): void {
      const now = performance.now();
      const renderDt = lastRenderTime > 0 ? Math.min((now - lastRenderTime) / 1000, 0.05) : 0;
      lastRenderTime = now;

      juice.update(renderDt, sim.state);
      applyCamera(renderDt, alpha);
      syncReplayGrade(renderDt);
      ambient.update(renderDt);
      // El balon moldea la red mientras esta dentro. La cesion la calcula la
      // simulacion; aqui solo se pinta donde y cuanto.
      const { pos } = sim.ball;
      if (pos.x < 0) field.nets.press("left", pos.y, sim.netGive);
      else if (pos.x > FIELD.WIDTH) field.nets.press("right", pos.y, sim.netGive);
      field.nets.update(renderDt);

      aimLayer?.setFirstTime(sim.firstTimeWindow());
      if (pairId) aimLayer2?.setFirstTime(sim.firstTimeWindow(pairId));
      trails.sync(alpha, sim.ball, sim.players, sim.possession.ownerId, renderDt);
      trails.update(renderDt);
      // Durante la repeticion, el aspecto grabado (el aro al pulsar, la carga…).
      playersLayer?.setReplayLook(sim.replayLook());
      playersLayer?.sync(alpha, sim.possession.ownerId, renderDt);
      varLine.sync(sim.state);
      ballLayer.sync(alpha, renderDt);
      aimLayer?.sync(alpha);
      aimLayer2?.sync(alpha);
      if (touch) {
        const me = sim.players.find((p) => p.id === sim.humanId);
        touch.sync(me?.chargeSteer ? me.charge : null);
      }
      /*
       * El aviso de "pasa atras" solo si el saque es TUYO: es el unico que
       * tiene el mando bloqueado, y avisar al que mira desde la otra mitad
       * seria contarle una regla que no le afecta.
       */
      hud.update(sim.state, renderDt, sim.possession.kickoff?.takerId === sim.humanId);
      radar.sync(sim.players, sim.ball, sim.humanId, seenField);
      if (stats) scoreboard.render(stats.snapshot(), sim.state.score, sim.humanId);
    },

    skipGoalPause(): void {
      sim.skipGoalPause();
    },

    resize(w: number, h: number): void {
      screenWidth = w;
      screenHeight = h;
      juice.resize(w, h);
      applyCamera();
      hud.layout(w, h);
      radar.layout(w, h);
    },

    suspend(): void {
      juice.audio.suspend();
    },

    resume(): void {
      juice.audio.resume();
    },

    dispose(): void {
      window.removeEventListener("keydown", unlockAudio);
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", onTabKey);
      window.removeEventListener("keyup", onTabKey);
      scoreboard.dispose();
      coach?.dispose();
      touch?.hide();
      touch?.dispose();
      lockstep?.dispose();
      juice.dispose();
      input.dispose();
      input2?.dispose();
      document.documentElement.classList.remove("fynta-mouse-aim");
      if (pairId) setCameraOverride(null);
      root.destroy({ children: true });
    },
  };
}
